import EmailIcon from "@mui/icons-material/Email";
import useExcelUrl from "app/useExcelUrl";
import {
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  GridColumns,
  PageHeader,
} from "common";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import moment from "moment";
import { ReviewEmailSentItem } from "./reviewEmailSentApi";
import ReviewEmailSentForm from "./ReviewEmailSentForm";
import useReviewEmailSentList from "./useReviewEmailSentList";

export interface ReviewEmailSentListProps extends Omit<
  DataGridBaseBaseProps,
  "id"
> {
  showHeader?: boolean;
}

export default function ReviewEmailSentList({
  showHeader = true,
  ...props
}: ReviewEmailSentListProps) {
  const { params, scope, editUrl, ...service } = useReviewEmailSentList();
  const { hasPermissions, canEditItem } = useAccess();
  const excelUrl = useExcelUrl("review-email-sents");
  let columns: GridColumns<ReviewEmailSentItem> = [
    {
      field: "booking_name",
      headerName: I18n.t("ReviewEmailSent.attributes.booking_name"),
      width: 120,
      renderCell: params => params.row.booking_name,
    },
    {
      field: "email",
      headerName: I18n.t("ReviewEmailSent.attributes.email"),
      flex: 1,
    },
    {
      field: "sent_at",
      headerName: I18n.t("ReviewEmailSent.attributes.sent_at"),
      width: 160,
      renderCell: params =>
        params?.row?.sent_at
          ? moment(params?.row?.sent_at).format("L LT")
          : null,
    },
    {
      field: "template_used",
      headerName: I18n.t("ReviewEmailSent.attributes.template_used"),
      width: 150,
    },
    {
      field: "error_code",
      headerName: I18n.t("ReviewEmailSent.attributes.error_code"),
      width: 120,
    },
    {
      field: "error_message",
      headerName: I18n.t("ReviewEmailSent.attributes.error_message"),
      flex: 1,
    },
    {
      field: "program_name",
      headerName: I18n.t("ReviewEmailSent.attributes.program_name"),
      flex: 1,
    },
    {
      field: "event_group.start_time",
      headerName: I18n.t("ReviewEmailSent.attributes.start_time"),
      flex: 1,
      renderCell: params =>
        params?.row?.start_time
          ? moment(params?.row?.start_time).format("L LT")
          : null,
    },
    {
      field: "booking_number",
      headerName: I18n.t("ReviewEmailSent.attributes.booking_number"),
      flex: 1,
    },
  ];

  return (
    <>
      {showHeader && (
        <PageHeader
          avatar={<EmailIcon />}
          title={I18n.t("ReviewEmailSent.title")}
        />
      )}
      <DataGrid
        {...service}
        columns={columns}
        id="review-email-sents"
        excelUrl={excelUrl}
        renderModalForm={props => (
          <ReviewEmailSentForm
            {...props}
            defaultValues={{
              sent_at: new Date().toISOString(),
            }}
          />
        )}
        modalProps={{ maxWidth: "lg" }}
        defaultState={{
          hidden_columns: [],
        }}
        {...props}
        //getHeaderName={field => I18n.t(`ReviewEmailSent.attributes.${field.replaceAll(".", "_")}`)}
        canCreate={hasPermissions("review_email_sent.create")}
        canDelete={hasPermissions([
          "review_email_sent.editany",
          "review_email_sent.editown",
        ])}
        canEdit={hasPermissions([
          "review_email_sent.editany",
          "review_email_sent.editown",
        ])}
        canView={false}
        canDeleteRow={params => canEditItem("review_email_sent", params.row)}
        canEditRow={params => canEditItem("review_email_sent", params.row)}
        canBatchActions={hasPermissions([
          "review_email_sent.editany",
          "review_email_sent.editown",
        ])}
        canHandleTrash={hasPermissions([
          "review_email_sent.editany",
          "review_email_sent.editown",
        ])}
      />
    </>
  );
}
